import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { CATEGORIES, Category } from '../../types';
import FormField from './FormField';
import TagInput from './TagInput';
import MediaUpload from './MediaUpload';

interface ContentFormProps {
  type: 'article' | 'video';
  onSubmit: (data: ContentFormData) => void;
}

export interface ContentFormData {
  title: string;
  description: string;
  content: string;
  media: string;
  category: string;
  subcategory: string;
  tags: string[];
}

export default function ContentForm({ type, onSubmit }: ContentFormProps) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState<ContentFormData>({
    title: '',
    description: '',
    content: '',
    media: '',
    category: '',
    subcategory: '',
    tags: [],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContentFormData, string>>>({});
  const [isPreview, setIsPreview] = useState(false);

  const selectedCategory = CATEGORIES.find(cat => cat.id === formData.category);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ContentFormData, string>> = {};
    
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.content.trim()) newErrors.content = 'Content is required';
    if (!formData.media) newErrors.media = `${type === 'article' ? 'Cover image' : 'Video'} is required`;
    if (!formData.category) newErrors.category = 'Category is required';
    if (selectedCategory?.subcategories && !formData.subcategory) {
      newErrors.subcategory = 'Subcategory is required';
    }
    if (formData.tags.length === 0) newErrors.tags = 'At least one tag is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const handleChange = (field: keyof ContentFormData, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
      // Reset subcategory when category changes
      ...(field === 'category' ? { subcategory: '' } : {})
    }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const renderEditor = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <FormField
        label="Title"
        error={errors.title}
        required
      >
        <input
          type="text"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          placeholder={`Enter ${type} title`}
          className={`
            w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
            ${errors.title ? 'border-red-500' : ''}
          `}
        />
      </FormField>

      <FormField
        label={type === 'article' ? 'Cover Image' : 'Video'}
        error={errors.media}
        required
      >
        <MediaUpload
          type={type === 'article' ? 'image' : 'video'}
          value={formData.media}
          onChange={(value) => handleChange('media', value)}
          error={!!errors.media}
        />
      </FormField>

      <FormField
        label="Description"
        error={errors.description}
        required
      >
        <textarea
          value={formData.description}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder={`Write a brief description...`}
          rows={3}
          className={`
            w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
            ${errors.description ? 'border-red-500' : ''}
          `}
        />
      </FormField>

      <FormField
        label="Content"
        error={errors.content}
        required
      >
        <textarea
          value={formData.content}
          onChange={(e) => handleChange('content', e.target.value)}
          placeholder={`Write your ${type} content...`}
          rows={12}
          className={`
            w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
            ${theme === 'dark'
              ? 'bg-gray-700 border-gray-600 text-gray-100'
              : 'bg-white border-gray-300 text-gray-900'
            }
            ${errors.content ? 'border-red-500' : ''}
          `}
        />
      </FormField>

      <div className="grid grid-cols-2 gap-6">
        <FormField
          label="Category"
          error={errors.category}
          required
        >
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className={`
              w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
              ${theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-gray-100'
                : 'bg-white border-gray-300 text-gray-900'
              }
              ${errors.category ? 'border-red-500' : ''}
            `}
          >
            <option value="">Select a category</option>
            {CATEGORIES.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </FormField>

        {selectedCategory?.subcategories && (
          <FormField
            label="Subcategory"
            error={errors.subcategory}
            required
          >
            <select
              value={formData.subcategory}
              onChange={(e) => handleChange('subcategory', e.target.value)}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
                ${errors.subcategory ? 'border-red-500' : ''}
              `}
            >
              <option value="">Select a subcategory</option>
              {selectedCategory.subcategories.map(sub => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </select>
          </FormField>
        )}
      </div>

      <FormField
        label="Tags"
        error={errors.tags}
        required
      >
        <TagInput
          tags={formData.tags}
          onChange={(tags) => handleChange('tags', tags)}
          error={!!errors.tags}
        />
      </FormField>

      <div className="flex justify-end space-x-4 pt-6">
        <button
          type="button"
          onClick={() => setIsPreview(!isPreview)}
          className={`
            px-4 py-2 rounded-lg transition-colors
            ${theme === 'dark'
              ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          {isPreview ? 'Edit' : 'Preview'}
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Publish
        </button>
      </div>
    </form>
  );

  const renderPreview = () => (
    <div className={`prose max-w-none ${theme === 'dark' ? 'prose-invert' : ''}`}>
      {formData.media && (
        <img
          src={formData.media}
          alt={formData.title}
          className="w-full h-64 object-cover rounded-lg mb-8"
        />
      )}
      <h1>{formData.title}</h1>
      <p className="text-lg font-medium">{formData.description}</p>
      <div dangerouslySetInnerHTML={{ __html: formData.content.replace(/\n/g, '<br>') }} />
      {formData.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-8">
          {formData.tags.map(tag => (
            <span
              key={tag}
              className={`
                px-3 py-1 rounded-full text-sm
                ${theme === 'dark'
                  ? 'bg-gray-700 text-gray-200'
                  : 'bg-gray-100 text-gray-700'
                }
              `}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className={`
      rounded-lg p-6
      ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}
    `}>
      {isPreview ? renderPreview() : renderEditor()}
    </div>
  );
}